import { Suspense } from 'react';
import { useEffect, useState } from 'react';
import { camelCase } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useSuspenseQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import LoadingOverlay from 'components/shared/table/LoadingOverlay';
import Tooltip from 'components/shared/Tooltip';
import { useProfile } from 'context/profile';
import Upload from 'hooks/useDirectUpload/Upload';
import useMutateWithToast from 'hooks/useMutateWithToast';
import Summarization from 'models/Summarization';
import { aiPromptsQueryKeys, indexAiPrompts } from 'services/api/aiPrompts';
import { createSummarization } from 'services/api/summarizations';
import { useMobileNavigationActions } from 'stores/mobileNavigationStore';
import theme from 'styles/theme/theme';
import { BodySmall, BodySmallBold, H1 } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';
import ArrowRight from 'svg/ArrowRight';
import HamburgerMenuIcon from 'svg/HamburgerMenuIcon';
import PurpleClippy from 'svg/PurpleClippy';

const CREATE_BUTTON_ID = 'create';
const CREATE_WORKSPACE_URL = 'https://www.olio.health/summarization-workspace-form';

function NewSummarization() {
  const { profile } = useProfile();
  const { setNavigationOpen } = useMobileNavigationActions();
  const navigate = useNavigate();
  const [aiPromptId, setAiPromptId] = useState<string>('');
  const [uploads, setUploads] = useState<Upload[]>([]);

  const queryParams = {
    'displayName.distinct': 'true',
    archived: false,
    sortBy: 'displayName',
    type: 'summary',
    client: profile.actingClientId,
    targetClassname: 'Summarization',
  };

  const { data: aiPromptsData } = useSuspenseQuery({
    queryKey: ['newSummarizationPage', aiPromptsQueryKeys.index(queryParams)],
    queryFn: ({ signal }) => indexAiPrompts({ ...queryParams }, signal),
  });

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: createSummarization,
    onSuccess: (summarization: Summarization) => {
      navigate(`/summarizations/${summarization.id}`);
    },
  });

  const handleGenerateSummary = () => {
    mutate({
      aiPromptId: aiPromptId,
      attachments: uploads,
    });
  };

  const handleUploadsChange = (newUploads: Upload[]) => {
    setUploads(newUploads);
  };

  const handlePillClick = (id: string) => {
    if (id === CREATE_BUTTON_ID) {
      window.open(CREATE_WORKSPACE_URL, '_blank');
    } else {
      setAiPromptId(id);
    }
  };

  const promptButtons = [
    ...(aiPromptsData?.data || []).map((prompt) => ({
      id: prompt.id,
      label: prompt.displayName,
      tag: camelCase(prompt.displayName),
      description: prompt.description,
    })),
    { id: CREATE_BUTTON_ID, label: '+ Create', tag: 'createSummarizationType', description: 'Add your own use case' },
  ];

  useEffect(() => {
    if (aiPromptsData?.data && aiPromptsData.data.length > 0 && !aiPromptId) {
      setAiPromptId(aiPromptsData.data[0].id);
    }
  }, [aiPromptsData, aiPromptId]);

  const canStartSummarizing = uploads.length > 0 && aiPromptId !== null;

  return (
    <>
      <ContentContainer>
        <MobileOnly>
          <MobileNavMenu variant='icon' onClick={() => setNavigationOpen(true)} data-cy='hamburgerIconButton'>
            <HamburgerMenuIcon />
          </MobileNavMenu>
        </MobileOnly>
        <Content>
          <HeaderSection>
            <Title>
              <AISparkleIcon width={30} height={29} />
              Summarize for <GradientText>quicker</GradientText> decision-making
            </Title>
            <Subtitle>Optimize your decision-making process by attaching patient documentation to summarize</Subtitle>
          </HeaderSection>

          <TypeSelectorSection>
            <BodySmallBold>Select a summarization type to guide our prompt for better results:</BodySmallBold>
            <TypePills>
              {promptButtons.map((button) => (
                <Tooltip key={button.id} text={button.description} disabled={!button.description} position='top'>
                  <TypePill
                    $selected={aiPromptId === button.id}
                    data-cy={button.tag}
                    onClick={() => handlePillClick(button.id)}>
                    <TypePillText $selected={aiPromptId === button.id} data-text={button.label}>
                      {button.label}
                    </TypePillText>
                  </TypePill>
                </Tooltip>
              ))}
            </TypePills>
          </TypeSelectorSection>

          <FileUploadContainer>
            <FileInputComponent
              onChange={handleUploadsChange}
              renderUploadsInternally
              draggingStyles={{
                backgroundColor: 'rgba(50, 83, 239, 0.05)',
                borderColor: theme.colors.primaryBlue,
              }}
              Icon={() => <PurpleClippy />}
            />
          </FileUploadContainer>

          <StartButton
            variant='primary'
            onClick={handleGenerateSummary}
            disabled={!canStartSummarizing}
            loading={isPending}>
            <AISparkleIcon width={14} height={14} color='white' />
            <BodySmallBold>Start Summarizing</BodySmallBold>
          </StartButton>
        </Content>

        <Footer>
          <ViewHistoryButton variant='outline-primary' onClick={() => navigate('/summarizations')}>
            <ButtonText>View History</ButtonText>
            <ArrowRight />
          </ViewHistoryButton>
        </Footer>
      </ContentContainer>
      <FooterBackground />
    </>
  );
}

export default function NewSummarizationPage() {
  return (
    <Suspense fallback={<Loading />}>
      <NewSummarization />
    </Suspense>
  );
}

const Loading = styled(LoadingOverlay)`
  position: relative;
`;

const MobileNavMenu = styled(Button)`
  position: absolute;
  top: 24px;
  left: 24px;
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.white};
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(174, 160, 240, 0.1) 70%,
    rgba(174, 160, 240, 0.15) 80%,
    rgba(94, 66, 225, 0.25) 100%
  );
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 40px 80px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled(H1)`
  font-weight: 900;
  font-size: 30px;
  margin: 0 0 16px 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;

  > svg {
    margin-top: 4px;
    margin-right: 10px;
    flex-shrink: 0;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(90deg, #9073ca 0%, #785bd4 48.575%, #593ee4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(BodySmall)`
  line-height: 22px;
  color: ${theme.colors.black75};
`;

const TypeSelectorSection = styled.div`
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const TypePills = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const TypePill = styled.button<{ $selected: boolean }>`
  padding: 8px 16px;
  border-radius: 36px;
  cursor: pointer;
  border: 1px solid;

  ${({ $selected }) =>
    $selected
      ? `
    background: linear-gradient(90deg, rgba(144, 115, 202, 0.1) 0%, rgba(120, 91, 212, 0.1) 48.575%, rgba(89, 62, 228, 0.1) 100%);
    border-color: #9073ca;
  `
      : `
    background: ${theme.colors.white};
    border-color: ${theme.colors.black25};

    &:hover {
      border-color: ${theme.colors.black50};
    }
  `}
`;

const TypePillText = styled.span<{ $selected: boolean }>`
  font-size: 12px;
  display: inline-block;
  position: relative;

  /* Reserve space for bold text using pseudo-element */
  &::before {
    content: attr(data-text);
    font-weight: 700;
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    display: block;
  }

  ${({ $selected }) =>
    $selected
      ? `
    font-weight: 700;
    background: linear-gradient(90deg, #9073ca 0%, #785bd4 48.575%, #593ee4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `
      : `
    font-weight: 400;
    color: ${theme.colors.black};
  `}
`;

const FileUploadContainer = styled.div`
  width: 100%;
  max-width: 792px;
  margin-bottom: 40px;
`;

const StartButton = styled(Button)`
  background: linear-gradient(90deg, rgba(107, 69, 184, 0.75) 0%, rgba(98, 65, 205, 0.871) 48.575%, #593ee4 100%);
  border: none;
  min-width: 200px;

  &:disabled {
    opacity: 0.25;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(90deg, rgba(107, 69, 184, 0.85) 0%, rgba(98, 65, 205, 0.95) 48.575%, #4a33c4 100%);
  }
`;

const Footer = styled.div`
  width: 100%;
  padding: 60px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FooterBackground = styled.div`
  width 100%;
  height: 100%;
  background: rgba(94, 66, 225, 0.25);
`;

const ViewHistoryButton = styled(Button)`
  border-radius: 20px;
  min-width: 179px;
  border-color: #9073ca;
  background: ${theme.colors.white};
  padding: 8px 24px;

  &:hover {
    border-color: #785bd4;
    background: ${theme.colors.white};
  }

  &:focus {
    border-color: #9073ca;
    background: ${theme.colors.white};
  }
`;

const ButtonText = styled(BodySmallBold)`
  background: linear-gradient(90deg, #9073ca 0%, #785bd4 48.575%, #593ee4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;
